import { setActiveCrawlerJob } from '@storeOld/reducers/crawler'
import { ErrorNotification } from '@storeOld/reducers/notification'
import { openSystemDialog } from '@storeOld/reducers/ui'
import axios from 'axios'
import { useDispatch } from 'react-redux'

import { useAuth } from '@/hooks'
import { crawlerClient } from '@/services/crawler'
import type { CrawlerJobId } from '@/types'
import { CANCEL_CRAWLING, STOP_CRAWLING } from '../../SystemDialogs/constants'
import { type CrawlerJobTarget, useCrawlerOpenJob } from './useCrawlerOpenJob'

// The crawler answers 404 for a job it no longer has: it finished and was removed
// while the modal was still up. Nothing is left to cancel or stop, so the job is
// simply gone. Any other failure means the job is still running out there.
const isJobGone = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404

/**
 * Stopping a crawl finishes it: whatever the crawler managed to collect is landed exactly
 * as a crawl that ran to the end would be. So this needs the same target — without it,
 * Stop used to land in a brand-new project, and the reset that path begins with wiped the
 * meta and access of the project the user was standing in.
 */
export const useCrawlerStopJob = (target?: Partial<CrawlerJobTarget>) => {
  const dispatch = useDispatch()
  const { openJob } = useCrawlerOpenJob(target)
  const { id: userId } = useAuth()

  const confirmCancel = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        openSystemDialog(CANCEL_CRAWLING, {
          callback: resolve,
        })
      )
    })

  const confirmStop = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        openSystemDialog(STOP_CRAWLING, {
          callback: resolve,
        })
      )
    })

  const dropActiveJob = () => {
    // @todo implement action
    // @ts-expect-error
    dispatch(setActiveCrawlerJob(null))
  }

  const cancelBackgroundJob = async (jobId: CrawlerJobId) => {
    const confirmed = await confirmCancel()
    if (!confirmed) {
      return
    }

    try {
      await crawlerClient.cancelActiveJob(jobId, userId)
    } catch (error) {
      if (!isJobGone(error)) {
        // The user asked to leave, so the modal still closes — but the crawl
        // is still running, so the job stays where the dashboard can show it.
        dispatch(ErrorNotification('Could not cancel the crawl'))
        return true
      }
    }

    dropActiveJob()
    return true
  }

  const stopBackgroundJob = async (jobId: CrawlerJobId) => {
    const confirmed = await confirmStop()
    if (!confirmed) {
      return
    }

    try {
      const result = await crawlerClient.stopActiveJob(jobId, userId)

      dropActiveJob()
      openJob(result.data)
      return true
    } catch (error) {
      // The pages are still on the crawler: leave the job in place so the user
      // can try again or wait for the crawl to end on its own.
      if (!isJobGone(error)) {
        dispatch(ErrorNotification('Could not stop the crawl, try again'))
        return
      }
    }

    // Finished in the meantime: its own `done` event has landed the pages.
    dropActiveJob()
    return true
  }

  return { stopBackgroundJob, cancelBackgroundJob }
}
